import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Users, Megaphone, Building2, TrendingUp, ShieldCheck } from "lucide-react";
import { brandLogos } from "@/data/siteData";

function Counter({ to, suffix = "+", duration = 1800, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let rafId;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * to));
      if (p < 1) rafId = requestAnimationFrame(tick);
      else setN(to);
    };
    rafId = requestAnimationFrame(tick);
    const finalTimer = setTimeout(() => setN(to), duration + 80);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(finalTimer);
    };
  }, [inView, to, duration]);

  return (
    <span ref={ref} data-testid={testId}>
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

/* Unique visual: Creators → dot/network pattern */
function NetworkDots({ inView }) {
  const dots = Array.from({ length: 24 });
  return (
    <div className="relative h-12 mt-5">
      <div className="grid grid-cols-8 gap-1.5 h-full items-center">
        {dots.map((_, i) => {
          const delay = 0.3 + (i % 8) * 0.04 + Math.floor(i / 8) * 0.08;
          return (
            <motion.span
              key={i}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={inView ? { opacity: i % 5 === 0 ? 1 : 0.45, scale: 1 } : {}}
              transition={{ duration: 0.4, delay }}
              className="w-1.5 h-1.5 rounded-full bg-[#2563EB]"
            />
          );
        })}
      </div>
    </div>
  );
}

/* Unique visual: Campaigns → single horizontal timeline */
function TimelineStrip({ inView }) {
  const markers = [0, 25, 50, 72, 100];
  return (
    <div className="relative h-12 mt-5">
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[2px] bg-slate-200 dark:bg-white/10" />
      <motion.div
        initial={{ width: 0 }}
        animate={inView ? { width: "100%" } : {}}
        transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-1/2 -translate-y-1/2 left-0 h-[2px] bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#3B82F6]"
      />
      <div className="absolute inset-0 flex items-center">
        {markers.map((pct, i) => (
          <motion.span
            key={pct}
            initial={{ opacity: 0, scale: 0.3 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.35, delay: 0.6 + i * 0.12 }}
            className="absolute w-2.5 h-2.5 rounded-full bg-[#2563EB] ring-2 ring-white dark:ring-[#0B1C3D]"
            style={{ left: `calc(${pct}% - 5px)` }}
          />
        ))}
      </div>
    </div>
  );
}

/* Unique visual: Brands → trust badge cluster */
function TrustBadges({ inView }) {
  return (
    <div className="relative h-12 mt-5 flex items-center gap-2.5">
      {[0, 1, 2, 3].map((i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 8 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.45, delay: 0.4 + i * 0.09 }}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-500/15 ring-1 ring-blue-100 dark:ring-blue-400/20 text-[#2563EB] dark:text-blue-300"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
        </motion.span>
      ))}
      <motion.span
        initial={{ opacity: 0, x: -6 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.4, delay: 0.78 }}
        className="text-[11px] font-semibold tracking-wider text-slate-500 dark:text-slate-400"
      >
        +96 more
      </motion.span>
    </div>
  );
}

const stats = [
  {
    id: "creators",
    value: 4000,
    suffix: "+",
    label: "Creators",
    sub: "Across South India",
    icon: Users,
    insight: "+55% YoY",
    visual: "network",
  },
  {
    id: "campaigns",
    value: 200,
    suffix: "+",
    label: "Campaigns",
    sub: "Delivered & Scaled",
    icon: Megaphone,
    insight: "87% on-time",
    visual: "timeline",
  },
  {
    id: "brands",
    value: 100,
    suffix: "+",
    label: "Brands",
    sub: "D2C · FMCG · Fintech",
    icon: Building2,
    insight: "78% repeat",
    visual: "badges",
  },
];

export default function SocialProof() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-80px" });
  const loop = [...brandLogos, ...brandLogos, ...brandLogos];

  // Parallax for background blobs
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const yA = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const yB = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yC = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section
      ref={sectionRef}
      id="proof"
      data-testid="social-proof-section"
      className="relative py-16 md:py-20 px-6 md:px-10 surface-1 overflow-hidden"
    >
      {/* Floating blurred gradient shapes (parallax) */}
      <motion.div
        aria-hidden
        style={{ y: yA }}
        className="pointer-events-none absolute -top-20 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-50 dark:opacity-30"
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%)",
          }}
        />
      </motion.div>
      <motion.div
        aria-hidden
        style={{ y: yB }}
        className="pointer-events-none absolute top-32 right-0 w-[480px] h-[480px] rounded-full blur-3xl opacity-40 dark:opacity-25"
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%)",
          }}
        />
      </motion.div>
      <motion.div
        aria-hidden
        style={{ y: yC }}
        className="pointer-events-none absolute bottom-10 left-1/3 w-[360px] h-[360px] rounded-full blur-3xl opacity-30 dark:opacity-20"
      >
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(96,165,250,0.25) 0%, transparent 70%)",
          }}
        />
      </motion.div>

      <div className="relative mx-auto max-w-7xl">
        {/* Heading row — fade-up on scroll */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end"
        >
          <div>
            <span className="eyebrow text-[#2563EB]">Social Proof</span>
            <h2 className="mt-4 font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.05] text-slate-900 dark:text-white">
              Built on Scale. <br />
              <span className="gradient-text">Proven Across South India.</span>
            </h2>
          </div>
          <div className="lg:pb-2">
            <p className="font-body text-base md:text-lg text-slate-600 dark:text-slate-300 max-w-md leading-relaxed">
              Numbers are a byproduct of clarity. The proof shows up in
              repeated work — D2C, FMCG and fintech brands trusting us with
              their next launch.
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 px-3 py-1 text-[#2563EB] dark:text-blue-200 font-semibold">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="eyebrow !tracking-[0.2em]">YoY +132%</span>
              </span>
            </div>
          </div>
        </motion.div>

        {/* Stat cards — minimal, clean, each with unique visual */}
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 28 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.15 + i * 0.15, ease: "easeOut" }}
                data-testid={`proof-stat-${s.id}`}
                className="group relative rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200/70 dark:border-white/[0.08] p-7 md:p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#2563EB]/30 hover:shadow-[0_20px_50px_-25px_rgba(37,99,235,0.35)]"
              >
                {/* Top row: icon + status chip */}
                <div className="flex items-center justify-between">
                  <motion.div
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.35 }}
                    className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-100 dark:border-blue-400/20 flex items-center justify-center text-[#2563EB] dark:text-blue-300"
                  >
                    <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                  </motion.div>
                  <span className="inline-flex items-center gap-1.5 text-[10.5px] font-medium text-slate-500 dark:text-slate-400">
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="absolute inset-0 rounded-full bg-emerald-500/60 animate-ping" />
                      <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-500" />
                    </span>
                    Live Data
                  </span>
                </div>

                {/* Main number */}
                <div className="mt-8 font-heading font-extrabold text-[56px] md:text-[64px] leading-[0.95] tracking-tight text-slate-900 dark:text-white">
                  <Counter to={s.value} suffix={s.suffix} testId={`counter-${s.id}`} />
                </div>

                {/* Label */}
                <div className="mt-2 text-lg md:text-xl font-semibold font-heading text-slate-900 dark:text-white">
                  {s.label}
                </div>

                {/* Supporting line */}
                <div className="mt-1 text-[13.5px] font-medium text-slate-500 dark:text-slate-400">
                  {s.sub}
                </div>

                {/* Unique visual per card */}
                {s.visual === "network" && <NetworkDots inView={inView} />}
                {s.visual === "timeline" && <TimelineStrip inView={inView} />}
                {s.visual === "badges" && <TrustBadges inView={inView} />}

                {/* Single insight chip */}
                <div className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#2563EB] dark:text-blue-300">
                  <TrendingUp className="w-3.5 h-3.5" />
                  {s.insight}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Brand logo marquee */}
      <div
        data-testid="brand-logo-marquee"
        className="relative mt-20 md:mt-24 mask-fade-x overflow-hidden"
      >
        <div className="flex items-center gap-14 md:gap-20 anim-scroll-x w-max whitespace-nowrap">
          {loop.map((b, i) => (
            <div
              key={`${b.name}-${i}`}
              className="shrink-0 flex items-center justify-center h-11 md:h-14 px-4 dark:bg-white/90 dark:rounded-xl dark:px-5 dark:py-2"
              title={b.name}
            >
              <img
                src={b.src}
                alt={b.name}
                loading="lazy"
                style={{ height: `${(b.scale || 1) * 100}%` }}
                className="w-auto max-w-[140px] md:max-w-[155px] object-contain opacity-85 hover:opacity-100 transition-opacity"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
